import React, { useState } from 'react'
import { authService } from '../../authService'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Avatar,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as AccessTimeIcon,
  VerifiedUser as VerifiedUserIcon,
  Shield as ShieldIcon,
  Lock as LockIcon,
  VpnKey as VpnKeyIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

const ProfileModal = ({ user, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Personal Info Form Data
  const [formData, setFormData] = useState({
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || ''
  })

  // Password Change State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  if (!isOpen || !user) return null

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setError('')
    setSuccess('')
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }))
    setError('')
  }

  const handleSave = async () => {
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const result = await authService.updateProfile(user.userid, formData)
      if (result.success) {
        setSuccess('Profile updated successfully!')
        setIsEditing(false)
        if (onUpdate) {
          onUpdate({ ...user, ...formData })
        }
        setTimeout(() => {
          setSuccess('')
        }, 3000)
      } else {
        setError(result.error || 'Failed to update profile')
      }
    } catch (err) {
      setError('An error occurred while updating profile')
    }
    setLoading(false)
  }

  const handleUpdatePassword = async () => {
    setError('')
    setSuccess('')

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setError('All password fields are required')
      return
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    setLoading(true)
    try {
      const result = await authService.changePassword(
        user.userid,
        passwordData.currentPassword,
        passwordData.newPassword
      )

      if (result.success) {
        setSuccess('Password updated successfully!')
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        })
        setTimeout(() => {
          setSuccess('')
        }, 3000)
      } else {
        setError(result.error || 'Failed to change password')
      }
    } catch (err) {
      setError('An error occurred while updating password')
    }
    setLoading(false)
  }

  const handleCancel = () => {
    setFormData({
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      email: user.email || '',
      phone: user.phone || '',
      address: user.address || ''
    })
    setIsEditing(false)
    setError('')
    setSuccess('')
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">My Profile</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="profile tabs">
          <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label="Personal Details" />
          <Tab icon={<LockIcon fontSize="small" />} iconPosition="start" label="Security & Password" />
        </Tabs>
      </Box>

      <DialogContent dividers>
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* User Avatar Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Avatar
            sx={{
              width: 88,
              height: 88,
              bgcolor: 'primary.main',
              fontSize: '2.2rem',
              fontWeight: 'bold',
              margin: '0 auto',
              mb: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            {user.firstname?.charAt(0)}{user.lastname?.charAt(0)}
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            {user.firstname} {user.lastname}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 0.5, color: 'text.secondary' }}>
            <ShieldIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
              {user.role} {user.department ? `(${user.department})` : ''}
            </Typography>
          </Box>
        </Box>

        {activeTab === 0 && (
          /* Tab 0: Personal Details */
          <Box sx={{ bgcolor: 'background.default', p: 3, borderRadius: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  First Name
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.firstname}
                    onChange={(e) => handleChange('firstname', e.target.value)}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="500">
                    {user.firstname}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Last Name
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.lastname}
                    onChange={(e) => handleChange('lastname', e.target.value)}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="500">
                    {user.lastname}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <EmailIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Email Address
                  </Typography>
                </Box>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                ) : (
                  <Typography variant="body1" fontWeight="500">
                    {user.email}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Phone Number
                  </Typography>
                </Box>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                ) : (
                  <Typography variant="body1" fontWeight="500">
                    {user.phone || 'Not provided'}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <BadgeIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    User ID
                  </Typography>
                </Box>
                <Typography variant="body1" fontWeight="500" color="primary">
                  {user.userid}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <HomeIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Address
                  </Typography>
                </Box>
                {isEditing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="Enter address"
                  />
                ) : (
                  <Typography variant="body1" fontWeight="500">
                    {user.address || 'Not provided'}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Account Created
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(user.datecreated)}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Last Login
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(user.lastlogin)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <VerifiedUserIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    Account Status
                  </Typography>
                </Box>
                <Chip
                  label={user.status}
                  color={user.status === 'active' ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          /* Tab 1: Security & Password */
          <Box sx={{ bgcolor: 'background.default', p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <VpnKeyIcon color="primary" fontSize="small" /> Update Password
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  size="small"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          edge="end"
                          size="small"
                        >
                          {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  size="small"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  helperText="Must be at least 6 characters long"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size="small"
                        >
                          {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  size="small"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {activeTab === 0 ? (
          isEditing ? (
            <>
              <Button onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button onClick={onClose}>
                Close
              </Button>
              <Button
                onClick={() => setIsEditing(true)}
                variant="contained"
                startIcon={<EditIcon />}
              >
                Edit Profile
              </Button>
            </>
          )
        ) : (
          <>
            <Button onClick={onClose} disabled={loading}>
              Close
            </Button>
            <Button
              onClick={handleUpdatePassword}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
            >
              Update Password
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ProfileModal

